import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { MdEco } from 'react-icons/md'

import {
  ButtonRebrand,
  ButtonRebrandVariant
} from 'src/components/atoms/ButtonRebrand'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { PillSwitchRebrand } from 'src/components/atoms/PillSwitchRebrand'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'

import { PackagesSectionRebrand as PackagesSectionRebrandSchema } from 'src/config/generated/sanitySchema'
import {
  PackageColor,
  PackageRow,
  PackageSlug,
  usePackages
} from 'src/hooks/usePackages'

// Note: section bg follows Figma primary-1 #FBF7F3. Closest colors.js token is
// `linen` (#F9F5F0); visually indistinguishable.

export type PackagesSectionRebrandProps = Omit<
  PackagesSectionRebrandSchema,
  '_type'
>

type Frequency = 'monthly' | 'annual'

// Marketing copy stays in code (decided 2026-06-23). The DB owns identity +
// pricing + image; this map owns the words. `designLabels` are the rounded
// marketing numbers shown on cards — they intentionally differ from the
// slider's "reality" numbers driven by the linked ProductPrice.
type MarketingCopy = {
  tagline: string
  impact: string
  perks: string[]
  ctaLabel: string
  pricePrefix: string
  designLabels: { monthly: string; annual: string }
  // Bold line shown above "What's included". Hardcoded per tier per stakeholder
  // copy — does not strictly match ProductPrice.VolumeMin (e.g. Kākano monthly
  // shows "8+" rather than the DB's 7). Leave as `null` to hide the line.
  treesLabels: { monthly: string | null; annual: string | null }
}

const MARKETING_BY_SLUG: Record<PackageSlug, MarketingCopy> = {
  kakano: {
    tagline: 'Plant the seed',
    impact: '$13 per tree',
    perks: [
      'Select a region for your trees',
      'Restoration project updates',
      'Marketing assets',
      'Profile page with live data'
    ],
    ctaLabel: 'Get started',
    pricePrefix: 'From',
    designLabels: { monthly: '$91', annual: '$988' },
    treesLabels: {
      monthly: '7+ native trees funded per month',
      annual: '76+ native trees funded per year'
    }
  },
  rakau: {
    tagline: 'Put down roots',
    impact: '$11.50 per tree',
    perks: [
      '12% discount on per-tree price',
      'Select a region for your trees',
      'Restoration project updates',
      'Marketing assets',
      'Profile page with live data'
    ],
    ctaLabel: 'Get started',
    pricePrefix: 'From',
    designLabels: { monthly: '$494', annual: '$4,991' },
    treesLabels: {
      monthly: '43+ native trees funded per month',
      annual: '434+ native trees funded per year'
    }
  },
  tipu: {
    tagline: 'Grow your impact',
    impact: '$10 per tree',
    perks: [
      '23% discount on per-tree price',
      'Everything in Kākano and Rākau, plus:',
      'Select projects to support',
      'Customised impact reporting',
      'Co-branded marketing assets',
      'Planting events (fees may apply)'
    ],
    ctaLabel: 'Get started',
    pricePrefix: 'From',
    designLabels: { monthly: '$1,000', annual: '$10,000' },
    treesLabels: {
      monthly: '100+ native trees funded per month',
      annual: '1,000+ native trees funded per year'
    }
  },
  ngahere: {
    tagline: 'Partner with us',
    impact: '$9.5 per tree',
    perks: [],
    ctaLabel: 'Learn more',
    pricePrefix: 'From',
    designLabels: { monthly: '$5,000', annual: '$50,000' },
    // Partnership card has its own layout (no perks list), so no trees label.
    treesLabels: { monthly: null, annual: null }
  }
}

const COLOR_STYLES: Record<
  PackageColor,
  {
    cardBg: string
    cardBorder: string
    chip: string
    badge: string
    button: ButtonRebrandVariant
  }
> = {
  lavender: {
    cardBg: 'bg-lavenderBright/20',
    cardBorder: 'border-lavenderBright',
    chip: 'bg-lavenderBright text-forestDeep',
    badge: 'bg-lavenderBright text-espresso',
    button: 'lavenderBright'
  },
  cream: {
    cardBg: 'bg-cream/50',
    cardBorder: 'border-cream',
    chip: 'bg-cream text-forestDeep',
    badge: 'bg-cream text-espresso',
    button: 'cream'
  },
  lime: {
    cardBg: 'bg-limeFresh/50',
    cardBorder: 'border-limeBright',
    chip: 'bg-limeFresh text-forestDeep',
    badge: 'bg-limeFresh text-forestDeep',
    button: 'limeFresh'
  },
  espresso: {
    cardBg: 'bg-espresso',
    cardBorder: 'border-espresso/50',
    chip: 'bg-cream text-espresso',
    badge: 'bg-espresso text-linen',
    button: 'cream'
  }
}

const PLACEHOLDER_IMG = '/img/hero/pohutakawa.jpg'

// TEMP preview: local pattern images per tier until the real AWS assets are
// uploaded and Package.ImageUrl is set. Remove this map + its usages once the
// S3 assets land. Exported so the package success screen uses the SAME per-tier
// images as the cards.
export const TEMP_IMAGE_BY_SLUG: Record<PackageSlug, string> = {
  kakano: '/img/hero/patterns/packages/kakano.png',
  rakau: '/img/hero/patterns/packages/rakau.png',
  tipu: '/img/hero/patterns/packages/tipu.png',
  ngahere: '/img/hero/patterns/patternDarkForest.png'
}

const FREQUENCY_OPTIONS: readonly { label: string; value: Frequency }[] = [
  { label: 'MONTHLY', value: 'monthly' },
  { label: 'ANNUAL', value: 'annual' }
]

// Prominent, solid price block shown at the top of the card body (Figma rough
// design 2026-06-25). Sized to read as the card's headline, not a small pill.
const PriceBlock = ({
  amount,
  cadence,
  className
}: {
  amount: string
  cadence: string
  className: string
}) => (
  <div
    className={`inline-flex items-baseline gap-[6px] whitespace-nowrap rounded-md px-6 py-4 ${className}`}
  >
    <span className='font-display-rebrand text-[24px] leading-[1.2] font-bold'>
      {amount}
    </span>
    <span className='font-body-rebrand text-[18px] leading-[1.4]'>
      {cadence}
    </span>
  </div>
)

// Impact pill overlaid on the package image (e.g. "At $13 per tree"). Uses the
// same tier colors as the price block (passed via className).
const ImpactBadge = ({
  text,
  className
}: {
  text: string
  className: string
}) => (
  <div className={`inline-flex items-center gap-2 px-3 py-2 ${className}`}>
    <MdEco className='shrink-0' size={20} />
    <span className='font-body-rebrand text-sm leading-[1.4]'>{text}</span>
  </div>
)

// Resolve full image URL. Package.ImageUrl is stored as a path (e.g.
// "assets/img/packages/kakano.jpg"); prefix with the env asset domain to
// hit S3/CDN. Falls back to a placeholder when the package has no image set.
export const resolveImageSrc = (imagePath?: string | null) => {
  if (!imagePath) return PLACEHOLDER_IMG
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://'))
    return imagePath
  // env reads via process.env so this stays SSR-safe.
  const domain = process.env.NEXT_PUBLIC_ASSETS_DOMAIN
  return domain ? `${domain}/${imagePath}` : `/${imagePath}`
}

// Append ?frequency=<monthly|annual> so /packages/[tier]'s hero block can pick
// the matching `monthlyAmount` / `annualAmount` field on the Sanity hero.
const buildLearnMoreHref = (slug: string, frequency: Frequency) =>
  `/packages/${slug}?frequency=${frequency}`

// CTA destination depends on whether the tier is self-serve. Self-serve goes
// through /get-started (carrying the active frequency so the slider matches
// what the user saw on the card); partnership (Ngahere) goes straight to the
// detail page where the contact flow lives.
const buildCtaHref = (pkg: PackageRow, frequency: Frequency) =>
  pkg.IsSelfServe
    ? `/get-started?tier=${pkg.Slug}&frequency=${frequency}`
    : `/packages/${pkg.Slug}`

const StandardPackageCard = ({
  pkg,
  frequency
}: {
  pkg: PackageRow
  frequency: Frequency
}) => {
  const slug = pkg.Slug as PackageSlug
  const copy = MARKETING_BY_SLUG[slug]
  const styles = COLOR_STYLES[pkg.Color as PackageColor]
  const learnMoreHref = buildLearnMoreHref(slug, frequency)
  const ctaHref = buildCtaHref(pkg, frequency)
  const priceLabel = copy.designLabels[frequency]
  const priceCadence = frequency === 'monthly' ? 'per month' : 'per year'

  // TEMP preview: local pattern per tier until the AWS asset lands.
  const imageSrc = TEMP_IMAGE_BY_SLUG[slug] ?? resolveImageSrc(pkg.ImageUrl)

  return (
    <div
      className={`relative flex w-full flex-col overflow-hidden pb-10 ${styles.cardBg}`}
    >
      <div className='relative h-[300px] w-full'>
        <Image src={imageSrc} alt='' fill className='object-cover' />
        {/* Impact badge + name + tagline overlaid on the image (top-left). */}
        <div className='absolute top-6 right-6 left-6 flex flex-col items-start gap-4'>
          <ImpactBadge text={copy.impact} className={styles.chip} />
          <div className='flex flex-col gap-1'>
            <TypographyRebrand variant='h4' color='forestDeep'>
              <span className='uppercase'>{pkg.DisplayName}</span>
            </TypographyRebrand>
            <TypographyRebrand variant='introduction' color='forestDeep'>
              {copy.tagline}
            </TypographyRebrand>
          </div>
        </div>
      </div>

      <div className='flex flex-1 flex-col gap-6 px-8 pt-6'>
        <div className='flex'>
          <PriceBlock
            amount={`${copy.pricePrefix} ${priceLabel}`}
            cadence={priceCadence}
            className={styles.chip}
          />
        </div>

        <div className='flex flex-col gap-2'>
          {copy.treesLabels[frequency] && (
            <p className='font-body-rebrand text-forestDeep text-base leading-[1.6] font-bold'>
              {copy.treesLabels[frequency]}
            </p>
          )}
          <TypographyRebrand variant='eyebrow' color='forestDeep'>
            What&rsquo;s included
          </TypographyRebrand>
          <ul className='ml-6 list-disc'>
            {copy.perks.map(perk => (
              <li
                key={perk}
                className='font-body-rebrand text-forestDeep text-base leading-[1.6]'
              >
                {perk}
              </li>
            ))}
          </ul>
        </div>

        <div className='mt-auto flex flex-wrap items-center gap-5 pt-2'>
          <Link href={ctaHref}>
            <ButtonRebrand variant={styles.button}>
              {copy.ctaLabel}
            </ButtonRebrand>
          </Link>
          <Link
            href={learnMoreHref}
            className='font-body-rebrand text-forestDeep shrink-0 whitespace-nowrap text-lg font-normal uppercase hover:underline'
          >
            Learn more
          </Link>
        </div>
      </div>
    </div>
  )
}

const PartnershipCard = ({ pkg }: { pkg: PackageRow }) => {
  const slug = pkg.Slug as PackageSlug
  const copy = MARKETING_BY_SLUG[slug]
  // Ngahere is contact-only, so `buildCtaHref` returns its detail page and
  // the frequency arg is unused — pass 'annual' to satisfy the signature
  // (Ngahere is annual-only anyway).
  const ctaHref = buildCtaHref(pkg, 'annual')
  // Ngahere is annual-only — always show the annual numbers regardless of
  // which tab is active on the section's Monthly/Annual switch.
  const priceLabel = copy.designLabels.annual
  const priceCadence = 'per year'

  return (
    <div className='bg-forestDeep relative flex w-full flex-col overflow-hidden md:flex-row md:items-stretch'>
      <div className='relative h-[280px] w-full md:h-auto md:w-[416px] md:self-stretch'>
        <Image
          src={TEMP_IMAGE_BY_SLUG[slug] ?? resolveImageSrc(pkg.ImageUrl)}
          alt=''
          fill
          className='object-cover'
        />
        {/* Dark gradient + name/tagline overlaid on the image (bottom-left,
            white) so it stays legible over the forest canopy. */}
        <div className='absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/50 to-transparent' />
        <div className='absolute bottom-6 left-6 flex flex-col gap-1'>
          <TypographyRebrand variant='h4' color='linen'>
            <span className='uppercase'>{pkg.DisplayName}</span>
          </TypographyRebrand>
          <TypographyRebrand variant='introduction' color='linen'>
            {copy.tagline}
          </TypographyRebrand>
        </div>
      </div>

      <div className='flex flex-1 flex-col items-start gap-6 px-8 py-12 md:pr-12'>
        <PriceBlock
          amount={`${copy.pricePrefix} ${priceLabel}`}
          cadence={priceCadence}
          className='bg-limeBright text-forestDeep'
        />
        <div className='text-linen flex flex-col'>
          <TypographyRebrand variant='paragraph' color='linen'>
            Design a partnership that meets your unique business objectives.
          </TypographyRebrand>
          <TypographyRebrand variant='paragraph' color='linen'>
            You&rsquo;ll enable long-term, meaningful environmental and social
            outcomes and receive bespoke impact reporting.
          </TypographyRebrand>
        </div>

        <Link href={ctaHref}>
          <ButtonRebrand variant='lime'>{copy.ctaLabel}</ButtonRebrand>
        </Link>
      </div>
    </div>
  )
}

export const PackagesSectionRebrand = ({
  heading,
  description
}: PackagesSectionRebrandProps) => {
  const [frequency, setFrequency] = useState<Frequency>('monthly')
  const { packages, loading } = usePackages()

  // Discriminate via the database flag, not the Color column — IsSelfServe
  // is the authoritative source of truth for "is this a slider-purchasable
  // tier" vs "contact-only partnership tier".
  const standardPackages = packages.filter(p => p.IsSelfServe)
  const partnershipPackage = packages.find(p => !p.IsSelfServe)

  return (
    <section className='bg-linen w-full py-6'>
      <div className='container mx-auto flex flex-col items-center gap-8 px-4 lg:px-20'>
        <div className='flex w-full max-w-[560px] flex-col items-center gap-5 text-center'>
          {heading ? (
            <TypographyRebrand variant='h2' color='forestDeep'>
              {heading}
            </TypographyRebrand>
          ) : null}
          {description ? (
            <TypographyRebrand variant='introduction' color='forestDeep'>
              {description}
            </TypographyRebrand>
          ) : null}
        </div>

        <PillSwitchRebrand
          options={FREQUENCY_OPTIONS}
          value={frequency}
          onChange={next => setFrequency(next as Frequency)}
          ariaLabel='Billing frequency'
        />

        <LoadingSpinner isLoading={loading}>
          <div className='flex w-full flex-col gap-10'>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
              {standardPackages.map(pkg => (
                <StandardPackageCard
                  key={`${frequency}-${pkg.Id}`}
                  pkg={pkg}
                  frequency={frequency}
                />
              ))}
            </div>
            {partnershipPackage && <PartnershipCard pkg={partnershipPackage} />}
          </div>
        </LoadingSpinner>
      </div>
    </section>
  )
}

export default PackagesSectionRebrand
